import { FC, useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Input, Popconfirm, Row, Switch, Typography } from 'antd';
import {
    TbAlertTriangle,
    TbArrowRight,
    TbCheck,
    TbCircle,
    TbCopy,
    TbExternalLink,
    TbInfoCircle,
    TbLink,
    TbMinusVertical,
    TbPlus,
    TbRefresh,
    TbServerCog,
    TbTrash,
    TbWorldCheck,
    TbWorldWww,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { workspaceService } from '@/services/database';
import { EmptySection, SmallTooltip } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import DomainConnectSetupButton from '@/shared/components/domains/DomainConnectSetupButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { WorkspaceCustomSubdomain, WorkspaceCustomSubdomains } from '@/types';

import AdvancedSettingsSectionHeader from '../../../components/AdvancedSettingsSectionHeader';

type ApiErrorLike = {
    response?: {
        data?: {
            error?: string;
        };
    };
};

const customSubdomainStatusLabel: Record<WorkspaceCustomSubdomain['status'], string> = {
    Active: 'Active',
    Disabled: 'Disabled',
    Misconfigured: 'Misconfigured',
    Pending: 'Pending DNS',
    Verifying: 'Verifying',
};

const getApiErrorDescription = (error: unknown, fallback: string) =>
    (error as ApiErrorLike)?.response?.data?.error ?? fallback;

const CustomSubdomainsSection: FC = (): JSX.Element => {
    const { selectedUser: user } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();

    const [enabled, setEnabled] = useState(false);
    const [hostnameInput, setHostnameInput] = useState('');
    const [customSubdomains, setCustomSubdomains] = useState<WorkspaceCustomSubdomains>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [verifyingCustomSubdomainId, setVerifyingCustomSubdomainId] = useState<string>();

    const canManage = ['Owner', 'Admin'].includes(String(user?.activeMember?.permission));
    const canUseCustomSubdomains = hasFeature('customSubdomains');
    const workspaceId = workspace?.id;
    const disabled = !canUseCustomSubdomains || !canManage || loading || saving || Boolean(verifyingCustomSubdomainId);
    const hasCustomSubdomains = Boolean(customSubdomains?.hostnames?.length);
    const shouldShowPanel = enabled || Boolean(customSubdomains?.enabled);
    const lastUpdatedLabel = customSubdomains?.updatedAt
        ? new Date(customSubdomains.updatedAt).toLocaleString()
        : undefined;

    const handleGetCustomSubdomains = useCallback(async () => {
        if (!workspaceId) {
            return;
        }

        try {
            setLoading(true);

            const response = await workspaceService.getCustomSubdomains(workspaceId);

            setCustomSubdomains(response);
            setEnabled(response.enabled);
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    }, [workspaceId]);

    useEffect(() => {
        handleGetCustomSubdomains();
    }, [handleGetCustomSubdomains]);

    const handleEnabledChange = async (checked: boolean) => {
        if (!workspace?.id) {
            return;
        }

        const previousEnabled = enabled;

        setEnabled(checked);

        try {
            setSaving(true);

            const response = await workspaceService.updateCustomSubdomains(workspace.id, { enabled: checked });

            setCustomSubdomains(response);
            setEnabled(response.enabled);
            toast.success(checked ? 'Custom subdomains enabled' : 'Custom subdomains disabled', {
                description: checked
                    ? 'Members can configure DNS records for approved workspace hostnames.'
                    : 'Custom hostname routing is disabled for this workspace.',
            });
        } catch (error) {
            setEnabled(previousEnabled);
            captureException(error);
            toast.error('Could not update custom subdomains', {
                description: getApiErrorDescription(error, 'Try again in a moment.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAddCustomSubdomain = async () => {
        if (!workspace?.id) {
            return;
        }

        const hostname = hostnameInput.trim();

        if (!hostname) {
            toast.error('Enter a hostname before adding a custom subdomain.');

            return;
        }

        try {
            setSaving(true);

            const response = await workspaceService.createCustomSubdomain(workspace.id, { hostname });

            setHostnameInput('');
            setCustomSubdomains(response);
            setEnabled(response.enabled);
            toast.success('Custom subdomain added', {
                description: 'Add the DNS records below, then verify the hostname.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Could not add custom subdomain', {
                description: getApiErrorDescription(error, 'Enter a fully-qualified hostname and try again.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleVerifyCustomSubdomain = async (customSubdomainId: string) => {
        if (!workspace?.id) {
            return;
        }

        try {
            setVerifyingCustomSubdomainId(customSubdomainId);

            const response = await workspaceService.verifyCustomSubdomain(workspace.id, customSubdomainId);

            setCustomSubdomains(response);
            toast.success('Custom subdomain checked', {
                description: 'DNS and certificate status were refreshed.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Could not verify custom subdomain', {
                description: getApiErrorDescription(error, 'Confirm the DNS records and try again.'),
            });
        } finally {
            setVerifyingCustomSubdomainId(undefined);
        }
    };

    const handleRemoveCustomSubdomain = async (customSubdomainId: string) => {
        if (!workspace?.id) {
            return;
        }

        try {
            setSaving(true);

            const response = await workspaceService.deleteCustomSubdomain(workspace.id, customSubdomainId);

            setCustomSubdomains(response);
            setEnabled(response.enabled);
            toast.success('Custom subdomain removed');
        } catch (error) {
            captureException(error);
            toast.error('Could not remove custom subdomain', {
                description: getApiErrorDescription(error, 'Try again in a moment.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCopyDnsRecord = async (value: string) => {
        await navigator.clipboard.writeText(value);
        toast('DNS value copied', { description: value });
    };

    const getStatusStyle = (status: WorkspaceCustomSubdomain['status']) => {
        if (status === 'Active') {
            return {
                background: token.colorSuccessBg,
                border: token.colorSuccessBorder,
                color: token.colorSuccessText,
                icon: <TbCheck />,
            };
        }

        if (status === 'Misconfigured') {
            return {
                background: token.colorErrorBg,
                border: token.colorErrorBorder,
                color: token.colorErrorText,
                icon: <TbAlertTriangle />,
            };
        }

        if (status === 'Disabled') {
            return {
                background: token.colorFillTertiary,
                border: token.colorBorder,
                color: token.colorTextTertiary,
                icon: <TbCircle />,
            };
        }

        return {
            background: token.colorInfoBg,
            border: token.colorInfoBorder,
            color: token.colorInfoText,
            icon: <TbRefresh />,
        };
    };

    const renderStatus = (customSubdomain: WorkspaceCustomSubdomain) => {
        const statusStyle = getStatusStyle(customSubdomain.status);

        return (
            <span
                style={{
                    alignItems: 'center',
                    background: statusStyle.background,
                    border: `1px solid ${statusStyle.border}`,
                    borderRadius: token.borderRadiusSM,
                    color: statusStyle.color,
                    display: 'inline-flex',
                    gap: '0.25rem',
                    padding: '0.125rem 0.375rem',
                }}
            >
                {statusStyle.icon}
                <small>{customSubdomainStatusLabel[customSubdomain.status]}</small>
            </span>
        );
    };

    const renderDnsRecord = (record: WorkspaceCustomSubdomain['dnsRecord']) => (
        <Row
            key={`${record.type}:${record.name}:${record.value}`}
            align='middle'
            gutter={[8, 8]}
            wrap={false}
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusSM,
                marginTop: '0.5rem',
                padding: '0.5rem',
            }}
        >
            <Col flex='none'>
                <Avatar
                    shape='square'
                    size={24}
                    style={{
                        background: token.colorBgContainer,
                        border: `1px solid ${token.colorBorder}`,
                        color: token.colorTextTertiary,
                    }}
                    icon={<TbServerCog size='0.875rem' />}
                />
            </Col>
            <Col flex='none'>
                <small style={{ color: token.colorTextTertiary }}>{record.type}</small>
            </Col>
            <Col flex='1 1 10rem'>
                <Typography.Text ellipsis>{record.name}</Typography.Text>
            </Col>
            <Col flex='none'>
                <TbArrowRight style={{ color: token.colorTextQuaternary, verticalAlign: '-0.125rem' }} />
            </Col>
            <Col flex='2 1 14rem'>
                <Typography.Text code copyable={false} ellipsis>
                    {record.value}
                </Typography.Text>
            </Col>
            <Col flex='none'>
                <Button type='text' size='small' onClick={() => handleCopyDnsRecord(record.value)}>
                    <TbCopy style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />
                </Button>
            </Col>
        </Row>
    );

    return (
        <>
            <AdvancedSettingsSectionHeader
                description='Route branded domains to your workspace and public shared resources with managed DNS validation.'
                icon={<TbWorldWww size='1rem' />}
                plan='enterprise'
                title='Custom Subdomains'
                extra={
                    <Switch
                        disabled={disabled}
                        loading={loading}
                        size='default'
                        style={{ verticalAlign: '-1rem' }}
                        checked={enabled}
                        checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.1rem' }} />}
                        unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.1rem' }} />}
                        onChange={handleEnabledChange}
                    />
                }
            />
            {shouldShowPanel && (
                <div
                    style={{
                        background: token.colorBgContainer,
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        marginBottom: '1.5rem',
                        padding: '1rem',
                    }}
                >
                    {enabled && (
                        <>
                            {lastUpdatedLabel && (
                                <small
                                    style={{
                                        color: token.colorTextTertiary,
                                        display: 'block',
                                        marginBottom: '0.75rem',
                                    }}
                                >
                                    Last updated {lastUpdatedLabel}
                                </small>
                            )}
                            {!canUseCustomSubdomains && (
                                <PlatformAlert
                                    showIcon
                                    type='warning'
                                    message='Custom subdomains require Enterprise'
                                    description='Upgrade the workspace plan before routing branded domains to this workspace.'
                                    style={{ marginBottom: '1rem' }}
                                />
                            )}
                            {!canManage && (
                                <PlatformAlert
                                    showIcon
                                    type='info'
                                    message='Owner or Admin permission required'
                                    description='Only workspace owners and admins can change custom subdomain routing.'
                                    style={{ marginBottom: '1rem' }}
                                />
                            )}
                            <Row gutter={[12, 12]} align='bottom' style={{ marginBottom: '1rem' }}>
                                <Col flex='auto'>
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Hostname
                                        <SmallTooltip
                                            title='Use a fully-qualified hostname like research.example.com. Wildcards and apex domains are intentionally blocked here.'
                                            placement='right'
                                            padding='0 0.5rem'
                                        >
                                            <TbInfoCircle
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    marginLeft: '0.25rem',
                                                    verticalAlign: '-0.1rem',
                                                }}
                                            />
                                        </SmallTooltip>
                                    </small>
                                    <Input
                                        disabled={disabled}
                                        prefix={
                                            <Avatar
                                                shape='square'
                                                size={16}
                                                style={{
                                                    background: token.colorBgElevated,
                                                    border: `1px solid ${token.colorBorder}`,
                                                    color: token.colorTextTertiary,
                                                    marginRight: '0.5rem',
                                                }}
                                                icon={<TbWorldWww size='0.875rem' />}
                                            />
                                        }
                                        placeholder='research.example.com...'
                                        value={hostnameInput}
                                        onChange={(event) => setHostnameInput(event.target.value)}
                                        onPressEnter={handleAddCustomSubdomain}
                                    />
                                </Col>
                                <Col flex='none'>
                                    <Button
                                        disabled={disabled}
                                        loading={saving}
                                        type='primary'
                                        onClick={handleAddCustomSubdomain}
                                    >
                                        Add Hostname
                                    </Button>
                                </Col>
                            </Row>
                            {hasCustomSubdomains ? (
                                <div>
                                    {customSubdomains?.hostnames.map((customSubdomain) => (
                                        <div
                                            key={customSubdomain.id}
                                            style={{
                                                background: token.colorBgContainer,
                                                border: `1px solid ${token.colorBorderSecondary}`,
                                                borderRadius: token.borderRadius,
                                                marginBottom: '0.75rem',
                                                padding: '0.875rem',
                                            }}
                                        >
                                            <Row align='middle' gutter={[12, 12]} wrap={false}>
                                                <Col flex='none'>
                                                    <Avatar
                                                        shape='square'
                                                        size={28}
                                                        style={{
                                                            background: token.colorBgElevated,
                                                            border: `1px solid ${token.colorBorder}`,
                                                            color: token.colorTextTertiary,
                                                        }}
                                                        icon={
                                                            customSubdomain.status === 'Active' ? (
                                                                <TbWorldCheck size='1rem' />
                                                            ) : (
                                                                <TbLink size='1rem' />
                                                            )
                                                        }
                                                    />
                                                </Col>
                                                <Col flex='auto'>
                                                    <Typography.Text strong>{customSubdomain.hostname}</Typography.Text>
                                                    <div style={{ marginTop: '0.25rem' }}>
                                                        {renderStatus(customSubdomain)}
                                                        {customSubdomain.cloudflare?.sslStatus && (
                                                            <small
                                                                style={{
                                                                    color: token.colorTextTertiary,
                                                                    marginLeft: '0.5rem',
                                                                }}
                                                            >
                                                                SSL {customSubdomain.cloudflare.sslStatus}
                                                            </small>
                                                        )}
                                                    </div>
                                                </Col>
                                                <Col flex='none'>
                                                    {workspace && customSubdomain.status !== 'Active' && (
                                                        <DomainConnectSetupButton
                                                            disabled={disabled}
                                                            prepareIntent={() =>
                                                                workspaceService.prepareCustomSubdomainDomainConnect(
                                                                    workspace.id,
                                                                    customSubdomain.id,
                                                                )
                                                            }
                                                            onReturn={() =>
                                                                handleVerifyCustomSubdomain(customSubdomain.id)
                                                            }
                                                            style={{ marginRight: '0.5rem' }}
                                                        />
                                                    )}
                                                    <Button
                                                        disabled={disabled}
                                                        loading={verifyingCustomSubdomainId === customSubdomain.id}
                                                        onClick={() => handleVerifyCustomSubdomain(customSubdomain.id)}
                                                        style={{ marginRight: '0.5rem' }}
                                                    >
                                                        <TbRefresh
                                                            style={{
                                                                color: token.colorTextTertiary,
                                                                marginRight: '0.25rem',
                                                                verticalAlign: '-0.125rem',
                                                            }}
                                                        />
                                                        Verify
                                                    </Button>
                                                    <Button
                                                        href={`https://${customSubdomain.hostname}`}
                                                        rel='noreferrer'
                                                        target='_blank'
                                                        style={{ marginRight: '0.5rem' }}
                                                    >
                                                        <TbExternalLink
                                                            style={{
                                                                color: token.colorTextTertiary,
                                                                verticalAlign: '-0.125rem',
                                                            }}
                                                        />
                                                    </Button>
                                                    <Popconfirm
                                                        title='Remove custom subdomain?'
                                                        description='Traffic will stop routing through this branded hostname after removal.'
                                                        okText='Remove'
                                                        okButtonProps={{ danger: true }}
                                                        onConfirm={() =>
                                                            handleRemoveCustomSubdomain(customSubdomain.id)
                                                        }
                                                    >
                                                        <Button danger disabled={disabled}>
                                                            <TbTrash style={{ verticalAlign: '-0.125rem' }} />
                                                        </Button>
                                                    </Popconfirm>
                                                </Col>
                                            </Row>
                                            <div style={{ marginTop: '0.75rem' }}>
                                                {renderDnsRecord(customSubdomain.dnsRecord)}
                                                {customSubdomain.verificationRecord &&
                                                    renderDnsRecord(customSubdomain.verificationRecord)}
                                            </div>
                                            {customSubdomain.error && (
                                                <PlatformAlert
                                                    showIcon
                                                    type='warning'
                                                    message='Provisioning needs attention'
                                                    description={customSubdomain.error}
                                                    style={{ marginTop: '0.75rem' }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <EmptySection
                                    height={16 * 14}
                                    icon={<TbWorldWww style={{ color: token.colorTextTertiary }} />}
                                    label='No Custom Subdomains'
                                    description='Add a branded hostname to receive DNS records and certificate verification instructions.'
                                    extra={
                                        <Button disabled={disabled} onClick={handleAddCustomSubdomain}>
                                            <TbPlus
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    marginRight: '0.25rem',
                                                    verticalAlign: '-0.125rem',
                                                }}
                                            />
                                            Add Hostname
                                        </Button>
                                    }
                                />
                            )}
                            {customSubdomains && !customSubdomains.cloudflareConfigured && (
                                <PlatformAlert
                                    showIcon
                                    type='warning'
                                    message='Cloudflare automation is not configured'
                                    description={`Hostname records will persist, but certificate automation requires CLOUDFLARE_ZONE_ID plus either CLOUDFLARE_API_TOKEN or CLOUDFLARE_API_KEY with CLOUDFLARE_EMAIL_ADDRESS. CNAME target: ${customSubdomains.cnameTarget}.`}
                                    style={{ marginTop: '1rem' }}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default CustomSubdomainsSection;
